import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { loginSchema } from '@/lib/validation';
import { authApi, ApiError } from '@/lib/pillarApi';
import { useAuthStore, type EffectiveSessionGeneration } from '@/store/auth';
import { runInBackground } from '@/lib/utils';
import { buildRequiredTotpSetupPath } from '@/lib/requiredTotpSetup';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { AnchorbaseLogo } from '@/components/branding/AnchorbaseLogo';
import { sessionSensitiveAuthApi } from '@/lib/sessionSensitiveAuthApi';
import { invalidateSessionSensitiveRequests } from '@/lib/sessionSensitiveRequestManager';
import {
    appendCloverOAuthLoginContext,
    buildCloverOAuthLaunchPath,
    parseCloverOAuthLoginContext,
} from '@/lib/clover/oauth-launch-context';
import {
    SessionUserProfileDetailedResponse,
    SessionUserProfileDetailedResponseSchema,
    LoginResponseSchema,
    JsonValuePrunedSchema,
    normalizeUserEmailInputForSubmission,
} from '@pillar-portal/api-types';

export const LoginPageFormSchema: z.ZodObject<{
    username: typeof loginSchema.shape.email;
    password: typeof loginSchema.shape.password;
}> = z.object({
    username: loginSchema.shape.email,
    password: loginSchema.shape.password,
});

export type PasswordLoginFormData = z.infer<typeof LoginPageFormSchema>;

export default function LoginPage() {
    const log = createComponentLogger('LoginPage');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prefilledEmail: string = normalizeUserEmailInputForSubmission(
        searchParams.get('email') ?? ''
    );
    const paymentRequestUuid: string = searchParams.get('payment_request_uuid')?.trim() ?? '';
    const returnToPayment: boolean = searchParams.get('return_to_payment') === '1';
    const cloverOAuthLaunchContext = parseCloverOAuthLoginContext(searchParams);
    const postLoginPath: string =
        cloverOAuthLaunchContext === null
            ? returnToPayment
                ? '/pay'
                : '/dashboard'
            : buildCloverOAuthLaunchPath(cloverOAuthLaunchContext);
    const queryClient = useQueryClient();
    const {
        user,
        logout: logoutStore,
        setUserForEffectiveSessionGeneration,
        getEffectiveSessionGeneration,
    } = useAuthStore();
    const [error, setError] = useState<string>('');
    const [isRedirecting, setIsRedirecting] = useState(false);
    const passwordLoginContinuationStarted = useRef(false);
    const hasAttemptedExistingSessionPaymentLinkAttach = useRef(false);
    const existingProfileGenerationRef = useRef<EffectiveSessionGeneration>(
        getEffectiveSessionGeneration()
    );
    // Check if user already has a valid session
    const { data: existingProfile, isLoading: isCheckingSession } =
        useQuery<SessionUserProfileDetailedResponse | null>({
            queryKey: ['profile'],
            queryFn: async () => {
                const response = await sessionSensitiveAuthApi.getProfile({
                    handle401AsSessionExpired: false,
                });
                if (response === null || response === undefined) {
                    return null;
                }
                existingProfileGenerationRef.current = getEffectiveSessionGeneration();
                return SessionUserProfileDetailedResponseSchema.parse(response);
            },
            enabled: !user && !passwordLoginContinuationStarted.current,
            retry: false,
            staleTime: 0,
            gcTime: 0,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            refetchOnReconnect: false,
        });

    // Redirect to dashboard or the payment page if user is already authenticated
    useEffect(() => {
        if (!passwordLoginContinuationStarted.current && (user || existingProfile)) {
            if (existingProfile && !user) {
                // If we fetched profile but user is not in store, set it
                setUserForEffectiveSessionGeneration(
                    existingProfile,
                    existingProfileGenerationRef.current
                );
            }
            if (
                existingProfile?.requires_password_change === true ||
                user?.requires_password_change === true
            ) {
                runInBackground(navigate('/profile/password', { replace: true }), (err) => {
                    log.error('Error navigating to password change', err);
                });
                return;
            }
            if (
                paymentRequestUuid.length > 0 &&
                !hasAttemptedExistingSessionPaymentLinkAttach.current
            ) {
                hasAttemptedExistingSessionPaymentLinkAttach.current = true;
                if (returnToPayment) {
                    setIsRedirecting(true);
                    runInBackground(navigate('/pay', { replace: true }), (err) => {
                        setIsRedirecting(false);
                        setError(
                            err instanceof Error && err.message.trim().length > 0
                                ? err.message
                                : 'Could not return to the payment page.'
                        );
                        log.error('Error returning to payment page', err);
                    });
                    return;
                }
                setIsRedirecting(true);
                runInBackground(
                    authApi
                        .completeCurrentCustomerPaymentLinkAttachment({
                            payment_request_uuid: paymentRequestUuid,
                        })
                        .then(() => navigate('/dashboard', { replace: true })),
                    (err) => {
                        setIsRedirecting(false);
                        setError(
                            err instanceof Error && err.message.trim().length > 0
                                ? err.message
                                : 'Could not attach the paid payment request to your account.'
                        );
                        log.error('Error attaching paid payment request to current session', err);
                    }
                );
                return;
            }
            runInBackground(navigate(postLoginPath, { replace: true }), (err) => {
                log.error('Error navigating after existing session check', err);
            });
        }
    }, [
        existingProfile,
        log,
        navigate,
        paymentRequestUuid,
        returnToPayment,
        postLoginPath,
        setUserForEffectiveSessionGeneration,
        user,
    ]);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<PasswordLoginFormData>({
        resolver: zodResolver(LoginPageFormSchema),
        defaultValues: {
            username: prefilledEmail,
            password: '',
        },
    });
    const usernameRegistration = register('username', {
        setValueAs: normalizeUserEmailInputForSubmission,
    });
    const passwordlessLoginSearchParams = new URLSearchParams(searchParams);
    const enteredEmail = normalizeUserEmailInputForSubmission(watch('username'));
    if (enteredEmail.length > 0) {
        passwordlessLoginSearchParams.set('email', enteredEmail);
    }
    const passwordlessLoginQuery = passwordlessLoginSearchParams.toString();
    const passwordlessLoginPath = `/login${
        passwordlessLoginQuery.length > 0 ? `?${passwordlessLoginQuery}` : ''
    }`;

    const loginMutation = useMutation({
        mutationFn: async (data: PasswordLoginFormData) => {
            const response = await authApi.login({
                email: data.username,
                password: data.password,
                ...(!returnToPayment && paymentRequestUuid.length > 0
                    ? { payment_request_uuid: paymentRequestUuid }
                    : {}),
            });
            return LoginResponseSchema.parse(response);
        },
        onSuccess: async (data) => {
            passwordLoginContinuationStarted.current = true;
            // Clear any stale cache from previous user session
            queryClient.clear();
            // Clear Zustand store to ensure fresh state
            logoutStore();
            invalidateSessionSensitiveRequests();
            setIsRedirecting(true);
            if (data.requires_mfa_setup || data.requires_totp_setup) {
                const nextSearchParams = new URLSearchParams();
                if (paymentRequestUuid.length > 0) {
                    nextSearchParams.set('payment_request_uuid', paymentRequestUuid);
                }
                if (returnToPayment) {
                    nextSearchParams.set('return_to_payment', '1');
                }
                appendCloverOAuthLoginContext(nextSearchParams, cloverOAuthLaunchContext);
                const target =
                    data.mfa_setup_activation_target ?? data.totp_setup_activation_target;
                const setupPath = buildRequiredTotpSetupPath(target);
                const setupUrl = new URL(setupPath, window.location.origin);
                for (const [key, value] of nextSearchParams.entries()) {
                    setupUrl.searchParams.set(key, value);
                }
                runInBackground(navigate(`${setupUrl.pathname}${setupUrl.search}`), (err) => {
                    log.error('Error navigating to MFA setup page', err);
                });
            } else if (data.requires_mfa || data.requires_totp) {
                const nextSearchParams = new URLSearchParams();
                if (paymentRequestUuid.length > 0) {
                    nextSearchParams.set('payment_request_uuid', paymentRequestUuid);
                }
                if (returnToPayment) {
                    nextSearchParams.set('return_to_payment', '1');
                }
                appendCloverOAuthLoginContext(nextSearchParams, cloverOAuthLaunchContext);
                const mfaMethods =
                    data.available_mfa_methods.length > 0
                        ? data.available_mfa_methods
                        : data.requires_totp
                          ? ['TOTP']
                          : [];
                if (mfaMethods.length > 0) {
                    nextSearchParams.set('mfa_methods', mfaMethods.join(','));
                }
                const nextTotpPath =
                    nextSearchParams.toString().length > 0
                        ? `/login/totp?${nextSearchParams.toString()}`
                        : '/login/totp';
                runInBackground(navigate(nextTotpPath), (err) => {
                    log.error('Error navigating to TOTP page', err);
                });
            } else {
                // When TOTP is not required, fetch profile to get complete user data including roles
                // then set user in store before redirecting to the post-login destination.
                try {
                    const profileResponse = await sessionSensitiveAuthApi.getProfile();
                    if (profileResponse) {
                        const profileData =
                            SessionUserProfileDetailedResponseSchema.parse(profileResponse);
                        setUserForEffectiveSessionGeneration(
                            profileData,
                            getEffectiveSessionGeneration()
                        );
                    }
                    window.location.assign(
                        data.requires_password_change ? '/profile/password' : postLoginPath
                    );
                } catch (err) {
                    // If profile fetch fails, still redirect and let ProtectedRoute handle it
                    // This ensures we don't get stuck on login page
                    log.error('Failed to fetch profile after login', err);
                    window.location.assign(
                        data.requires_password_change ? '/profile/password' : postLoginPath
                    );
                }
            }
        },
        onError: (err: Error) => {
            if (err instanceof ApiError) {
                setError(err.message);
            } else {
                setError(`Error: ${JsonValuePrunedSchema.parse(err)}`);
            }
            log.error('Error logging in', err);
        },
    });

    const onSubmit = (data: PasswordLoginFormData) => {
        const normalizedUsername: string = normalizeUserEmailInputForSubmission(data.username);
        setValue('username', normalizedUsername);
        setError('');
        loginMutation.mutate({ ...data, username: normalizedUsername });
    };

    if (error.length > 0 && (user || existingProfile)) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
                <Card className="w-full max-w-md">
                    <CardHeader className="space-y-1">
                        <CardTitle>Account linking unavailable</CardTitle>
                        <CardDescription>
                            We could not attach this paid payment request to your current account.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                            {error}
                        </div>
                        <Button
                            type="button"
                            className="w-full"
                            onClick={() => navigate('/dashboard', { replace: true })}
                        >
                            Continue to dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Show loading screen while checking for existing session or during redirect
    if (isCheckingSession || isRedirecting || user || existingProfile) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6 pb-6">
                        <div className="flex flex-col items-center justify-center space-y-4">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                            <p className="text-sm text-muted-foreground">
                                {isRedirecting ? 'Signing you in...' : 'Checking session...'}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center p-4 bg-gradient-to-br from-blue-50 to-purple-50">
            <Card className="w-full max-w-md">
                <CardHeader className="space-y-1">
                    <CardTitle className="text-3xl font-bold text-center flex justify-center">
                        <AnchorbaseLogo width={280} />
                    </CardTitle>
                    <CardDescription className="text-center">
                        Enter your credentials to access your account
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="username">Email</Label>
                            <Input
                                id="username"
                                type="email"
                                placeholder="you@example.com"
                                autoComplete="username"
                                {...usernameRegistration}
                                onBlur={(event) => {
                                    const normalizedUsername: string =
                                        normalizeUserEmailInputForSubmission(
                                            event.currentTarget.value
                                        );
                                    event.currentTarget.value = normalizedUsername;
                                    setValue('username', normalizedUsername, {
                                        shouldDirty: true,
                                        shouldTouch: true,
                                    });
                                    return usernameRegistration.onBlur(event);
                                }}
                                disabled={loginMutation.isPending}
                            />
                            {errors.username && (
                                <p className="text-sm text-destructive">
                                    {errors.username.message}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="Enter your password"
                                autoComplete="current-password"
                                {...register('password')}
                                disabled={loginMutation.isPending}
                            />
                            {errors.password && (
                                <p className="text-sm text-destructive">
                                    {errors.password.message}
                                </p>
                            )}
                        </div>

                        {error && (
                            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                                {error}
                            </div>
                        )}

                        <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
                            {loginMutation.isPending ? 'Signing in...' : 'Sign In'}
                        </Button>

                        <div className="text-center">
                            <a
                                href="/forgot-password"
                                className="text-sm text-primary hover:underline"
                            >
                                Forgot Password?
                            </a>
                        </div>
                        <div className="text-center">
                            <a
                                href={passwordlessLoginPath}
                                className="text-sm text-primary hover:underline"
                            >
                                Log in with your email
                            </a>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
